import React from 'react';
import { useAuth, useIsAdmin } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import ProfileAvatarEditor from '../components/ProfileAvatarEditor';
import MediaAssetsSection from '../components/profile/MediaAssetsSection';
import ArchivedChats from '../components/profile/ArchivedChats';
import TimezoneSettings from '../components/profile/TimezoneSettings';
import DisplaySettings from '../components/profile/DisplaySettings';
import UsageSection from '../components/profile/UsageSection';
import AboutYouSection from '../components/profile/AboutYouSection';
import EmailNotificationsSection from '../components/profile/EmailNotificationsSection';
import HelpLinksSection from '../components/profile/HelpLinksSection';

const AccountSettings: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();

  // Detect sign-in method from Firebase Auth provider data
  const providerId = auth.currentUser?.providerData?.[0]?.providerId;
  const signInMethod = providerId === 'google.com'
    ? 'Signed in with Google'
    : providerId === 'password'
    ? 'Signed in with email'
    : 'Signed in';

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
      <div className="max-w-6xl mx-auto p-8">
        {/* NO BACK LINK (owner 2026-08-24): the activity bar is permanent chrome on every signed-in page, so
            the way back is always on screen and a per-page duplicate only competes with it. */}
        {/* Page Title */}
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-8">
          Settings
        </h1>

        {/* 2-column card grid on desktop (fills the width); single column on mobile. Wide cards span both. */}
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* Profile Summary — full width header */}
          <div className="lg:col-span-2 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-secondary-900 dark:bg-secondary-700 rounded-full flex items-center justify-center text-white text-xl font-medium flex-shrink-0 overflow-hidden">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name || ''}
                    referrerPolicy="no-referrer"
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : (
                  user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {user?.name || user?.email}
                </h2>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  {user?.email}
                </p>
                <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
                  {signInMethod}
                </p>
              </div>
            </div>
            <ProfileAvatarEditor />
          </div>

          {/* Usage — one quiet bar showing how much of the allowance is left; no per-action pricing. */}
          <UsageSection />

          {/* About you — optional self-info the agent cast references warmly */}
          <AboutYouSection />

          {/* Display & reading — font size + font theme presets for readability (device preference) */}
          <DisplaySettings />

          {/* Timezone — display preference (backend stays UTC) */}
          <TimezoneSettings />

          {/* Email notifications — subscribe/unsubscribe from lifecycle emails (weekly summary, etc.) */}
          <EmailNotificationsSection />

          {/* Help and legal — the tutorials/FAQ/privacy/terms links (and the admin cost console) that
              used to be unlabelled icons in the sidebar rail (RF3-QUIET, owner 2026-08-23). */}
          <HelpLinksSection isAdmin={isAdmin} />

          {/* Assets — the user's shared media + per-asset analytics (full width — media grid) */}
          <div className="lg:col-span-2"><MediaAssetsSection /></div>

          {/* Archived group chats — soft-deleted; reopen / restore / delete permanently (full width — list) */}
          <div className="lg:col-span-2"><ArchivedChats /></div>

        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
